import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "solar-web-design-colorado",
  metaTitle: "Solar Installer Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom solar web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Solar Installer Web Design Colorado",
  eyebrow: "Solar Installer Web Design — Colorado",
  h1: (
    <>
      Solar Installer <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded solar websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Solar websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first solar sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Solar Installer web design in Colorado",
    body: "Solar Installer web design is the practice of building conversion-focused, mobile-first websites for solar businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with savings calculator above the fold." },
    { title: "Savings calculator", body: "Bill + roof-size calculator that estimates savings and books a consult in 60 seconds." },
    { title: "Service pages per offering", body: "Residential, commercial, battery, EV charging, off-grid, system monitoring — each ranks." },
    { title: "Incentive + tax credit pages", body: "CO + federal tax credit, net metering, and Xcel rebate pages capture long-tail traffic." },
    { title: "Online consultation booking", body: "Real-time booking with Calendly or your CRM." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Savings calculator drives the consult", body: "Solar is sold on payback period. A calculator above the fold qualifies the lead and books the consult." },
    { heading: "Incentive content captures research traffic", body: "CO + federal tax credit content earns long-tail traffic from buyers in research mode." },
    { heading: "Battery + EV charging are the new searches", body: "Storage and EV charger add-ons have higher margin and lower competition than panel installs." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a solar website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a solar website?", a: "Typical timeline is 4–6 weeks for a 12–20 page solar site with full service and service-area pages. Larger multi-location solar businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs solar businesses?", a: "No — we build solar sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Solar Installer Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for solar businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/solar-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
